import * as React from "react";

import { FloatingPortal } from "@floating-ui/react";
import { toast } from "@replohq/lattice/components/toast/Toast";
import { cn } from "@replohq/lattice/utils/cn";
import { skipToken } from "@tanstack/react-query";
import {
  DEBUG_DEV_PANEL_BASE_CLASS,
  useDebugOverlayIsVisible,
} from "@v2/features/admin/debugPanelChrome";
import {
  getUserOperatorId,
  LOGIN_AS_PARAM,
  operatorIdMatchesUser,
  useLoginAsState,
} from "@v2/features/debug/loginAs";
import { getTokenFromStorage } from "@v2/features/infra/auth";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { userRouter } from "@v2/trpc/routes/user";
import { routes } from "@v2/utils/router";
import { storeToken } from "@v2/utils/store-token";
import { matchPath, useLocation } from "react-router-dom";

function getUserNameOrEmail(user: {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}) {
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  if (user.firstName) {
    return user.firstName;
  }
  return user.email ?? null;
}

/**
 * The full Login-As chrome — the candidate "Login as <user>" offer, the active
 * "Logged in as / Return to" indicator, and the exit hatch. Renders as bare
 * blocks (no card wrapper) so it can be embedded in the agent-ui debug box or
 * wrapped in a standalone card via `GlobalLoginAsChrome`.
 *
 * Login-As is superuser-only; the backend enforces this on every call, and we
 * also gate the candidate offer/lookup here so a non-superuser never fires the
 * (doomed) request or sees the offer.
 */
function LoginAsChrome() {
  const [loginAs, setLoginAs] = useLoginAsState();
  const startLoginAs = userRouter.startLoginAs.useMutation();
  const { user: currentUser } = useCurrentUser();

  const canLoginAs = Boolean(currentUser?.isSuperuser);
  const isLoginAsActive = loginAs?.status === "active";
  const guestUserOperatorId =
    loginAs?.status === "candidate" || loginAs?.status === "active"
      ? loginAs.guestUserOperatorId
      : null;
  // NOTE (Gabe, 2026-07-01): A candidate that refers to the current user is a
  // no-op offer ("log in as yourself"); the url-sync hook clears these on
  // agent-ui paths, but gate the render too so a stale self-candidate never
  // shows elsewhere.
  const isSelfCandidate = Boolean(
    loginAs?.status === "candidate" &&
    currentUser &&
    operatorIdMatchesUser(loginAs.guestUserOperatorId, currentUser.id),
  );
  const { data: guestUser } = userRouter.findLoginAsTarget.useQuery(
    canLoginAs && guestUserOperatorId && !isSelfCandidate
      ? { userOperatorId: guestUserOperatorId }
      : skipToken,
  );
  const currentUserName = currentUser ? getUserNameOrEmail(currentUser) : null;
  const currentUserEmail = currentUser?.email ?? null;
  const currentUserId = currentUser?.id ?? null;
  const activeLoginAsOriginalName = isLoginAsActive
    ? (loginAs.originalName ?? loginAs.originalEmail ?? null)
    : null;
  const activeLoginAsOriginalEmail = isLoginAsActive
    ? (loginAs.originalEmail ?? null)
    : null;
  const exitLoginAsAriaLabel = currentUserName
    ? `Log out of ${currentUserName}`
    : "Log out";

  const connectAsGuestUser = (guestUserOperatorId: string) => {
    // Capture the superuser's own token BEFORE swapping — it's the escape hatch back.
    const originalToken = getTokenFromStorage();
    if (!originalToken) {
      toast.error({
        title: "Login as failed",
        description: "Missing current session token.",
      });
      return;
    }
    startLoginAs.mutate(
      { userOperatorId: guestUserOperatorId },
      {
        onSuccess: (result) => {
          setLoginAs({
            status: "active",
            guestUserOperatorId,
            originalUserOperatorId: currentUser
              ? getUserOperatorId(currentUser.id)
              : undefined,
            guestEmail: result.email,
            originalEmail: currentUser?.email,
            originalName: currentUser ? getUserNameOrEmail(currentUser) : null,
            originalToken,
          });
          storeToken(result.token);
          window.location.reload();
        },
      },
    );
  };

  const exitLoginAs = () => {
    storeToken(loginAs?.status === "active" ? loginAs.originalToken : null);
    setLoginAs(
      loginAs?.status === "active" && loginAs.guestUserOperatorId
        ? {
            status: "candidate",
            guestUserOperatorId: loginAs.guestUserOperatorId,
          }
        : null,
    );
    // Clean up the URL before reload; localStorage controls whether the guest stays available.
    const url = new URL(window.location.href);
    url.searchParams.delete(LOGIN_AS_PARAM);
    window.location.replace(url.toString());
  };

  // NOTE (Gabe, 2026-07-01): A foreign opid pasted while already
  // impersonating. The session holds the guest's token and the login-as
  // routes are superuser-gated, so switching first restores the original
  // superuser token (the tRPC client reads the token from storage per
  // request), then connects to the new target with it — one click swaps
  // guests. On failure we land back as the original superuser with the target
  // queued as an ordinary candidate, so the pasted opid is never lost.
  const pendingGuestUserOperatorId = isLoginAsActive
    ? (loginAs.pendingGuestUserOperatorId ?? null)
    : null;

  const switchToPendingGuest = async (guestUserOperatorId: string) => {
    if (loginAs?.status !== "active") {
      return;
    }
    const {
      originalToken,
      originalUserOperatorId,
      originalEmail,
      originalName,
    } = loginAs;
    storeToken(originalToken);
    try {
      const result = await startLoginAs.mutateAsync({
        userOperatorId: guestUserOperatorId,
      });
      setLoginAs({
        status: "active",
        guestUserOperatorId,
        originalUserOperatorId,
        guestEmail: result.email,
        originalEmail,
        originalName,
        originalToken,
      });
      storeToken(result.token);
    } catch {
      setLoginAs({ status: "candidate", guestUserOperatorId });
    }
    const url = new URL(window.location.href);
    url.searchParams.delete(LOGIN_AS_PARAM);
    window.location.replace(url.toString());
  };

  const dismissPendingGuest = () => {
    if (loginAs?.status !== "active") {
      return;
    }
    setLoginAs({ ...loginAs, pendingGuestUserOperatorId: undefined });
  };

  return (
    <>
      {currentUserName && (
        <div
          className={cn(
            "flex flex-col border-b px-2 py-1",
            isLoginAsActive ? "border-amber/40" : "border-black/10",
          )}
        >
          <span className="text-[9px] tracking-wide text-debug">
            Logged in as
          </span>
          <div className="flex items-center justify-between gap-2">
            <span className="min-w-0 truncate text-[10px] font-semibold text-debug-strong">
              {currentUserName}
            </span>
            {isLoginAsActive && (
              <button
                type="button"
                onClick={exitLoginAs}
                aria-label={exitLoginAsAriaLabel}
                title={exitLoginAsAriaLabel}
                className="shrink-0 rounded bg-amber/20 px-1.5 py-0.5 text-[10px] font-medium hover:bg-amber/40"
              >
                Log out
              </button>
            )}
          </div>
          {currentUserEmail && currentUserEmail !== currentUserName && (
            <span className="min-w-0 truncate font-mono text-[9px] text-debug">
              {currentUserEmail}
            </span>
          )}
          {isLoginAsActive &&
            !currentUserEmail &&
            loginAs.guestEmail !== currentUserName && (
              <span className="min-w-0 truncate font-mono text-[9px] text-debug">
                {loginAs.guestEmail}
              </span>
            )}
          {currentUserId && (
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard.writeText(currentUserId).then(() => {
                  toast.success({ title: "Copied user ID" });
                });
              }}
              title={`Copy user ID — ${currentUserId}`}
              className="min-w-0 truncate text-left font-mono text-[9px] text-debug hover:text-debug-strong"
            >
              {currentUserId}
            </button>
          )}
        </div>
      )}
      {activeLoginAsOriginalName && (
        <div className="flex min-w-0 flex-col border-b border-amber/40 px-2 py-1 leading-tight">
          <span className="text-[9px] tracking-wide text-debug">Return to</span>
          <span className="min-w-0 truncate text-[10px] text-debug-strong">
            {activeLoginAsOriginalName}
          </span>
          {activeLoginAsOriginalEmail &&
            activeLoginAsOriginalEmail !== activeLoginAsOriginalName && (
              <span className="min-w-0 truncate font-mono text-[9px] text-debug">
                {activeLoginAsOriginalEmail}
              </span>
            )}
        </div>
      )}
      {pendingGuestUserOperatorId && (
        <div className="flex items-center justify-between gap-2 border-b border-amber/40 px-2 py-1">
          <span className="flex min-w-0 flex-col leading-tight">
            <span className="text-[9px] tracking-wide text-debug">
              Switch to
            </span>
            <span className="min-w-0 truncate font-mono text-[10px] text-debug-strong">
              {pendingGuestUserOperatorId}
            </span>
          </span>
          <span className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              disabled={startLoginAs.isPending}
              onClick={() => {
                void switchToPendingGuest(pendingGuestUserOperatorId);
              }}
              className="rounded bg-amber/20 px-1.5 py-0.5 text-[10px] font-medium hover:bg-amber/40 disabled:opacity-50"
            >
              Switch
            </button>
            <button
              type="button"
              onClick={dismissPendingGuest}
              aria-label="Dismiss switch offer"
              className="rounded px-1 py-0.5 text-[10px] text-debug hover:text-debug-strong"
            >
              ×
            </button>
          </span>
        </div>
      )}
      {canLoginAs && loginAs?.status === "candidate" && !isSelfCandidate && (
        <div className="flex items-center justify-between gap-2 border-b border-amber/40 px-2 py-1">
          <span className="flex min-w-0 flex-col leading-tight">
            <span className="text-[9px] tracking-wide text-debug">
              Login as
            </span>
            <span className="min-w-0 truncate text-[10px] text-debug-strong">
              {guestUser?.name ?? guestUser?.email ?? "…"}
            </span>
            {guestUser?.name && (
              <span className="min-w-0 truncate font-mono text-[9px] text-debug">
                {guestUser.email}
              </span>
            )}
          </span>
          <button
            type="button"
            disabled={startLoginAs.isPending}
            onClick={() => {
              connectAsGuestUser(loginAs.guestUserOperatorId);
            }}
            className="shrink-0 rounded bg-amber/20 px-1.5 py-0.5 text-[10px] font-medium hover:bg-amber/40 disabled:opacity-50"
          >
            Connect
          </button>
        </div>
      )}
    </>
  );
}

/**
 * Standalone, self-gating Login-As chrome for non-agent-ui surfaces (dashboard,
 * editor). Mounted once at the app root so a superuser who swapped on agent-ui
 * always keeps a visible "operating as <user>" indicator and an exit hatch,
 * no matter where they navigate. Suppressed on agent-ui paths, where the chrome
 * is already embedded in the debug box.
 *
 * Follows the same debug-overlay toggle as the rest of the debug chrome, and
 * only mounts when there's actual Login-As state (candidate or active) so it
 * never clutters surfaces with a bare identity card.
 */
export function GlobalLoginAsChrome() {
  const { pathname } = useLocation();
  const { visible: overlaysVisible } = useDebugOverlayIsVisible();
  const [loginAs] = useLoginAsState();
  const { isAuthenticated } = useCurrentUser();

  const isAgentUiPath = Boolean(matchPath(`${routes.agent.root}/*`, pathname));

  // NOTE (Gabe, 2026-07-01): Gate on login-as state rather than the current
  // user's superuser bit: while impersonating, the current user IS the guest
  // (usually not a superuser), and the chrome must stay visible so the
  // operator keeps the exit hatch. Login-as state can only be created by a
  // superuser flow in the first place. Signed out (e.g. on /auth/login), every
  // row inside is empty and the card collapses to a bare yellow strip.
  if (!overlaysVisible || isAgentUiPath || !loginAs || !isAuthenticated) {
    return null;
  }

  return (
    <FloatingPortal>
      {/* z-[99999] stacks this dev exit-hatch above modals/overlays (which sit at z-50). pointer-events-auto is required on top: a Radix modal dialog sets `pointer-events: none` on <body> while open, which this portaled node would otherwise inherit and become unclickable. */}
      <div className="pointer-events-auto fixed bottom-4 left-4 z-[99999] max-w-[calc(100vw-1rem)]">
        <div
          className={cn(
            DEBUG_DEV_PANEL_BASE_CLASS,
            "flex w-fit min-w-[12rem] max-w-[min(100vw-2rem,18rem)] flex-col overflow-hidden text-debug-strong opacity-80 shadow-sm transition-opacity hover:opacity-100",
          )}
        >
          <LoginAsChrome />
        </div>
      </div>
    </FloatingPortal>
  );
}
